import { showNotification } from '@mantine/notifications';
import type { Ref } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { debounceLocalDefinitionSnapshot } from '../Documentation/utils';
import type { IProseMirrorEditorProps } from '../Editor/outline/src';

import { trackEvent } from '@repo/common/analytics';
import { embeds } from '@repo/secoda-editor';
import { useAuthUser, useWorkspace } from '../../api';
import { useDebouncedCallbackWithFlush } from '../../hooks/useDebouncedCallbackWithFlush';
import { useFeatureFlags } from '../../utils/featureFlags';
import { useEditorClickLink } from '../../utils/hook/useEditorClickLink';
import { RichMarkdownEditor } from '../Editor/outline/src';
import {
	DISABLE_EXTENSIONS_FOR_LIMITED_EDITOR,
	DISABLE_INPUT_EXTENSIONS_FOR_LIMITED_EDITOR,
} from './RichEditor.constants';
import { uploadFile } from './RichEditor.utils';

export interface IRichEditorProps
	extends Pick<
		IProseMirrorEditorProps,
		| 'disableExtensions'
		| 'enableExtensions'
		| 'template'
		| 'editorRef'
		| 'canMentionSecodaAi'
	> {
	readOnly: boolean;
	dataTestId?: string;
	placeholder?: string;
	emptyLinePlaceholder?: string;
	emptyDocPlaceholder?: string;
	autoFocus?: boolean;
	initialValue?: string;
	disableTopGap?: boolean;
	onChangeCallback?: (value?: string, saveRemotely?: boolean) => void;
	id?: string;
	limited?: boolean;
	className?: string;
	proseMirrorRef?: Ref<RichMarkdownEditor>;
	focusedCommentID?: string;
	onCreatePlaceholderComment?: (selectedText: string) => void;
	onClickComment?: (commentID: string) => void;
	singleLineEditor?: boolean;
	showMentionMenuButton?: boolean;
	enableScrollToHash?: boolean;
	value?: string;
	disableResourceLinking?: boolean;
	disableChangeCallbackDebounce?: boolean;
	onChangeCallbackDebounceWait?: number;
	onPaste?: (e: React.ClipboardEvent<HTMLDivElement>) => void;
}

export const RichEditor = memo((props: IRichEditorProps) => {
	const {
		id,
		dataTestId,
		onChangeCallback,
		autoFocus,
		initialValue,
		disableTopGap,
		readOnly,
		emptyLinePlaceholder,
		emptyDocPlaceholder,
		disableExtensions,
		enableExtensions,
		limited = false,
		className = 'rich-text-editor',
		proseMirrorRef = null,
		focusedCommentID,
		onCreatePlaceholderComment,
		onClickComment,
		singleLineEditor,
		showMentionMenuButton,
		enableScrollToHash = false,
		template,
		value,
		disableResourceLinking = false,
		disableChangeCallbackDebounce = false,
		onChangeCallbackDebounceWait = 150,
		editorRef,
		canMentionSecodaAi,
		onPaste,
	} = props;

	if (value && (onChangeCallback || !readOnly)) {
		throw new Error(
			'RichEditor: value prop is only supported in readOnly mode'
		);
	}

	const location = useLocation();
	const { user } = useAuthUser();
	const { workspace } = useWorkspace();
	const [defaultValue] = useState(initialValue ?? '');

	const initialScrollTo: string | undefined = enableScrollToHash
		? location.hash
		: undefined;

	const [scrollTo] = useState(initialScrollTo);

	const { editorAiSummaryBlock } = useFeatureFlags();

	const disableExtensionsWithFeatureFlag = useMemo(() => {
		const extensions =
			(limited ? DISABLE_EXTENSIONS_FOR_LIMITED_EDITOR : disableExtensions) ??
			[];
		if (!editorAiSummaryBlock) {
			extensions.push('ai_summary');
		}
		return extensions;
	}, [disableExtensions, editorAiSummaryBlock, limited]);

	const disableInputExtensions = useMemo(
		() => (limited ? DISABLE_INPUT_EXTENSIONS_FOR_LIMITED_EDITOR : []),
		[limited]
	);

	let debounceChangeWait = onChangeCallback
		? onChangeCallbackDebounceWait
		: 4000;
	if (disableChangeCallbackDebounce) {
		debounceChangeWait = 0;
	}

	const handleChange = useDebouncedCallbackWithFlush(
		(changedValue: () => string | undefined) => {
			if (id) {
				debounceLocalDefinitionSnapshot(id, changedValue());
			}

			if (onChangeCallback) {
				onChangeCallback(changedValue());
			}
		},
		debounceChangeWait
	);

	const onShowToast = useCallback(
		(message: string) =>
			showNotification({
				message: `${message}`,
			}),
		[]
	);

	const onClickLink = useEditorClickLink();

	const onTrackEvent = useCallback(
		(eventName: string, properties: Record<string, string> = {}) => {
			if (eventName) {
				trackEvent(eventName, properties, user, workspace);
			}
		},
		[user, workspace]
	);

	const commonProps: IProseMirrorEditorProps = {
		id,
		defaultValue,
		dataTestId: dataTestId ?? 'rich-text-editor',
		embeds,
		readOnly,
		autoFocus,
		readOnlyWriteCheckboxes: true,
		onChange: handleChange,
		focusedCommentID,
		disableTopGap,
		uploadFile,
		emptyLinePlaceholder:
			emptyLinePlaceholder ?? 'Write documentation or press "/" for actions...',
		emptyDocPlaceholder,
		onShowToast,
		onClickLink,
		onCreatePlaceholderComment,
		onClickComment,
		className,
		disableExtensions: disableExtensionsWithFeatureFlag,
		enableExtensions,
		disableInputExtensions,
		template,
		singleLineEditor,
		showMentionMenuButton,
		scrollTo,
		value,
		disableResourceLinking,
		onTrackEvent,
		editorRef,
		canMentionSecodaAi,
		onPaste,
	};

	return <RichMarkdownEditor {...commonProps} ref={proseMirrorRef} />;
});
RichEditor.displayName = 'RichEditor';
